/**
 * The web half of a number <-> measurement element conversion
 * (docs/plans/measurements.md decision #8; keepr-api docs/SCHEMA.md "Card
 * definition"). When a SAVED element's dataType moves from number | decimal |
 * integer to measurement, `PATCH /api/card-definitions/{id}` needs
 * `options.convertFrom: { unit }` — the single (non-composite) unit of the
 * element's measure the existing values were entered in — and backfills the
 * items. The reverse move needs `options.convertTo: { unit }`. Both are
 * request-time instructions the API strips before storing; nothing here is
 * ever read back from a saved definition.
 *
 * planConversion decides, per element, whether the transition applies and
 * resolves the unit (the picked one, else the default unit when it is
 * single). withConversionInstructions builds the payload elements from it,
 * so a stale instruction (the author flipped the type and flipped it back)
 * never travels. Elements match their saved counterpart by the name the API
 * will give them (effectiveElementNames), exactly as the API matches.
 * describeConversion mirrors the API's history sentence so the save toast
 * and the history row read alike.
 */
import { getMeasure, isComposite, isUnitOf, unitsOf } from './units';
import { defaultUnitFor, groupUnitOptions } from './measurements';
import { allowedCodes, defaultCodeOf } from './currency';
import { currencyOption } from './money';
import { SHAPE_INSTRUCTION_KEY, conversionIsCurrent, describeShapeConversion, keptConversion } from './shapeChange';
import { withInertChoicesAsStored } from './choiceValues';

export const NUMERIC_TYPES = ['number', 'decimal', 'integer'];
export const CONVERSION_KEYS = ['convertFrom', 'convertTo'];

/** The coded refusals a conversion can draw from the API, shown inline on the element. */
export const CONVERSION_ERROR_CODES = ['conversion_unit_required', 'invalid_unit', 'conversion_currency_required', 'invalid_currency', 'backfill_too_large'];

const isNumeric = dataType => NUMERIC_TYPES.includes(dataType);

/** An unset dataType IS short text everywhere else in the card editor. */
const typeOf = element => element?.dataType || 'text-small';

/** A unit id that is a single (non-composite) unit of `measureId`. */
export const isSingleUnitOf = (unitId, measureId) => (
	typeof unitId === 'string' && isUnitOf(unitId, measureId) && !isComposite(unitId)
);

/**
 * Select options for the single (non-composite) units of a measure, in
 * registry order — grouped under family headings for a measure that splits
 * (volume, energy, pressure, data size), flat for every other one.
 */
export const conversionUnitOptions = (measureId) => groupUnitOptions(
	unitsOf(measureId).filter(unit => !unit.parts)
);

/**
 * Select options for a currency conversion: the element's own currencies,
 * in the author's order ("USD — US dollar").
 */
export const conversionCurrencyOptions = (codes) => (codes || []).map(currencyOption).filter(Boolean);

/**
 * The currency half of planConversion (docs/plans/currency.md "Conversion"):
 * a number crossing into a currency element names the currency its existing
 * numbers are in (`convertFrom: { currency }`, one of the NEW element's
 * currencies); a currency element crossing back names the currency whose
 * amounts become plain numbers (`convertTo: { currency }`, one of the SAVED
 * element's — amounts in any other currency are skipped by the API). The
 * default currency is preselected, as measurement preselects its default unit.
 */
function planCurrencyConversion(key, pickedOptions, codeOptions) {
	const currencies = allowedCodes(codeOptions);
	const picked = pickedOptions?.[key]?.currency;
	const fallback = defaultCodeOf(codeOptions);
	const currency = currencies.includes(picked) ? picked : (currencies.includes(fallback) ? fallback : null);
	return { key, kind: 'currency', currencies, currency };
}

/**
 * planConversion(element, savedElement) -> null | { key, measure, unit }
 *   | { key, kind: 'currency', currencies, currency }
 *
 * `savedElement` is the element as the loaded definition holds it (matched by
 * name, as the API matches). null when there is no conversion: a new element,
 * an unchanged type, or a type change that does not cross the numeric /
 * measurement line. Otherwise `key` is the instruction the payload needs,
 * `measure` the measure id the unit must belong to, and `unit` the resolved
 * single unit — the author's pick when it fits, else the default unit when it
 * is single, else null (the save is blocked until one is chosen).
 */
export function planConversion(element, savedElement) {
	if (!element || !savedElement) return null;
	const next = typeOf(element);
	const previous = typeOf(savedElement);
	if (next === previous) return null;

	if (isNumeric(previous) && next === 'measurement') {
		const measure = getMeasure(element.options?.measure);
		if (!measure) return { key: 'convertFrom', measure: null, unit: null };
		const picked = element.options?.convertFrom?.unit;
		const fallback = defaultUnitFor(element.options);
		const unit = isSingleUnitOf(picked, measure.id) ? picked
			: (isSingleUnitOf(fallback, measure.id) ? fallback : null);
		return { key: 'convertFrom', measure: measure.id, unit };
	}

	if (isNumeric(previous) && next === 'currency') {
		return planCurrencyConversion('convertFrom', element.options, element.options);
	}

	if (previous === 'currency' && isNumeric(next)) {
		// As for a measurement: a saved element that never named a currency
		// holds nothing convertible, so it is a plain type change.
		if (!allowedCodes(savedElement.options).length) return null;
		return planCurrencyConversion('convertTo', element.options, savedElement.options);
	}

	if (previous === 'measurement' && isNumeric(next)) {
		// The API converts back only when the previous measure is known; an
		// element that never had one is a plain type change.
		const measure = getMeasure(savedElement.options?.measure);
		if (!measure) return null;
		const picked = element.options?.convertTo?.unit;
		const fallback = defaultUnitFor(savedElement.options);
		const unit = isSingleUnitOf(picked, measure.id) ? picked
			: (isSingleUnitOf(fallback, measure.id) ? fallback : null);
		return { key: 'convertTo', measure: measure.id, unit };
	}

	return null;
}

/** Elements of the loaded definition keyed by name, for planConversion. */
export const indexSavedElements = (elements) => Object.fromEntries(
	(elements || []).filter(el => el && el.name).map(el => [el.name, el])
);

// The API's element slug (keepr-api utils/cardDefUtils.js slugify): NFKD,
// diacritics stripped, runs of non-alphanumerics to one dash, trimmed, lower.
export const apiSlug = (str) => (str ? String(str) : '')
	.normalize('NFKD')
	.replace(/[\u0300-\u036f]/g, '')
	.replace(/[^A-Za-z0-9]+/g, '-')
	.replace(/^-+|-+$/g, '')
	.toLowerCase();

/**
 * effectiveElementNames(elements) -> string[]
 *
 * The name each element will have once saved, by index: its own when it has
 * one, else the name the API will assign from its singular label
 * (populateElementNames: the slug, made unique against every name already
 * in the list, in list order, 'element' when the label is empty). The API
 * plans conversions by that name, so an element deleted and re-added under
 * the same label IS the saved element to it — and to the editor.
 */
export function effectiveElementNames(elements) {
	const list = elements || [];
	const taken = new Set(list.filter(el => el && typeof el.name === 'string' && el.name.trim()).map(el => el.name));
	return list.map(el => {
		if (!el) return '';
		if (typeof el.name === 'string' && el.name.trim()) return el.name;
		const seed = apiSlug(el.label?.singular) || 'element';
		let candidate = seed;
		let suffix = 2;
		while (taken.has(candidate)) candidate = `${seed}-${suffix++}`;
		taken.add(candidate);
		return candidate;
	});
}

/**
 * namespacedElementNames(elements, key) -> string[]
 *
 * The same question for an ELEMENT SET (keepr-api utils/elementSchema.js
 * populateElementNames with a namespace): every name comes out
 * `<key>_<slug>` — the client's name with any `<key>_` prefix stripped, else
 * the singular label, slugified ('element' when empty) — unique among the
 * prefixed names in list order (`contact_phone`, `contact_phone-2`). What
 * the set page shows under each label, and the name the card write path
 * will see. `key` is the set's own key, or the slug the API will derive
 * from the set's name before its first save.
 */
export function namespacedElementNames(elements, key) {
	const prefix = `${key || 'set'}_`;
	const taken = new Set();
	return (elements || []).map(el => {
		if (!el) return '';
		const own = typeof el.name === 'string' && el.name.trim() ? el.name.trim() : '';
		const raw = own ? (own.startsWith(prefix) ? own.slice(prefix.length) : own) : (el.label?.singular || '');
		const slug = apiSlug(raw) || 'element';
		let candidate = `${prefix}${slug}`;
		let suffix = 2;
		while (taken.has(candidate)) candidate = `${prefix}${slug}-${suffix++}`;
		taken.add(candidate);
		return candidate;
	});
}

/**
 * The key the API derives for a new element set from its name (keepr-api
 * utils/elementSetUtils.js resolveKey): the element-name slug, 'set' when
 * the name has none, made unique against `takenKeys` — the keys of the sets
 * already in the same scope (global, or the same collection) — by suffixing
 * (`contact-2`), exactly like a card key. Every stored element name carries
 * the key, so the page must preview the one the API will actually assign.
 */
export function elementSetKeyFromName(name, takenKeys = []) {
	const seed = apiSlug(name) || 'set';
	const taken = new Set(takenKeys || []);
	let candidate = seed;
	let suffix = 2;
	while (taken.has(candidate)) candidate = `${seed}-${suffix++}`;
	return candidate;
}

/**
 * The elements as the PATCH should carry them: every conversion key stripped,
 * then the one that applies added back with its resolved unit. `missing`
 * lists the elements whose conversion has no unit yet (block the save).
 *
 * "Allow multiple"'s answer (`options.convertValues`, owner ruling B16 —
 * utils/shapeChange.js) is the author's, picked in the element's dialog: it
 * travels only while the element still flips the way it was picked for, so
 * a switch turned back never carries a stale one.
 *
 * A choice list on a non-Choice element travels exactly as its saved copy
 * stores it, or not at all (utils/choiceValues withInertChoicesAsStored,
 * KPE-033): the API refuses a save that adds or changes an inert list.
 */
export function withConversionInstructions(elements, savedByName) {
	const missing = [];
	const names = effectiveElementNames(elements);
	const out = (elements || []).map((element, i) => {
		if (!element || !element.options) return element;
		const saved = savedByName?.[names[i]];
		const plan = planConversion(element, saved);
		const hasKey = CONVERSION_KEYS.some(key => key in element.options);
		// The flip's answer, a file element's 'detach' (PR 10b), or both as
		// one array (`['first', 'detach']`, the 10b contract).
		const staleShape = SHAPE_INSTRUCTION_KEY in element.options && !conversionIsCurrent(element, saved);
		if (!plan && !hasKey && !staleShape) return element;
		const options = { ...element.options };
		CONVERSION_KEYS.forEach(key => { delete options[key]; });
		if (staleShape) {
			const kept = keptConversion(element, saved);
			if (kept === undefined) delete options[SHAPE_INSTRUCTION_KEY];
			else options[SHAPE_INSTRUCTION_KEY] = kept;
		}
		if (plan && plan.kind === 'currency') {
			// No currencies yet: there is nothing to choose from — the save is
			// blocked for the currency list instead.
			if (plan.currency) options[plan.key] = { currency: plan.currency };
			else missing.push({ element: names[i], key: plan.key, kind: 'currency', currencies: plan.currencies });
		} else if (plan) {
			// `measure` null: the element has no measure yet, so there is no
			// unit to choose — the save is blocked for the measure instead.
			if (plan.unit) options[plan.key] = { unit: plan.unit };
			else missing.push({ element: names[i], key: plan.key, measure: plan.measure });
		}
		return { ...element, options };
	});
	return { elements: withInertChoicesAsStored(out, savedByName, names), missing };
}

/**
 * "weight converted to measurement (mass, lb): 1,240 items converted, 3
 * skipped (3 range)" — the same sentence the API writes to history, built
 * from the response's `conversion` summary.
 */
export function describeConversion(conversion) {
	if (conversion?.choice) return describeShapeConversion(conversion);
	const c = conversion || {};
	const n = v => Number(v || 0).toLocaleString('en-US');
	const noun = c.converted === 1 ? 'item' : 'items';
	// A currency conversion names its currency instead of a measure and a
	// unit, and counts the values it had to round to the currency's decimals
	// (keepr-api utils/measurementUtils.js describeConversion, verbatim).
	let text = c.currency
		? `${c.element} converted to ${c.to} (${c.currency}): ${n(c.converted)} ${noun} converted${c.rounded ? `, ${n(c.rounded)} rounded to ${c.currency}'s decimal places` : ''}`
		: `${c.element} converted to ${c.to} (${c.measure}, ${c.unit}): ${n(c.converted)} ${noun} converted`;
	if (c.skipped) {
		const reasons = Object.entries(c.skippedReasons || {}).map(([k, v]) => `${n(v)} ${String(k).replace(/_/g, ' ')}`);
		text += `, ${n(c.skipped)} skipped${reasons.length ? ` (${reasons.join(', ')})` : ''}`;
	}
	if (c.incomplete) text += ' — stopped part-way (finish with the backfillMeasurement maintenance task)';
	return text;
}

/** Every conversion summary a save response carries, first one included. */
export const conversionsFromResponse = (data) => {
	if (Array.isArray(data?.conversions) && data.conversions.length) return data.conversions;
	return data?.conversion ? [data.conversion] : [];
};
